import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingController, PopoverController } from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { EmployerDto, ExaTokenDto, UserGrantDto, UserLoginDto, UserRestService } from '../api/exa';
import { NavUrls } from '../enums/nav-urls';
import { ExaGrant } from '../model/exa-grant';
import { AlertService } from './alert.service';
import { AppInitService } from './app-init.service';
import { DialogService } from './dialog.service';
import { ErrorHandlerService } from './error-handler.service';
import { StorageService } from './storage.service';
import { firstValueFrom } from 'rxjs';
import { EmployerService } from './employer.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly loginTokenName = 'auth_service_login_token';

  private onAuthenticateSubject: BehaviorSubject<UserGrantDto> = new BehaviorSubject(null);
  public selectedEmployer$ = new BehaviorSubject<EmployerDto>(null);
  private tokenRefresher = null;
  private loadingCtrl = null;
  private authenticatedUserToken: ExaTokenDto = null;
  private saveToken = false;
  public authUserData: UserGrantDto = null;
  public islogIn = false;

  constructor(
    private userRestService: UserRestService,
    private log: NGXLogger,
    private errHandler: ErrorHandlerService,
    private dialogService: DialogService,
    private alertService: AlertService,
    // private popoverController: PopoverController,
    private storage: StorageService,
    private configService: AppInitService,
    private router: Router
  ) {}

  public async login(loginData: UserLoginDto, stayLoggedIn: boolean): Promise<boolean> {
    try {
      this.log.debug(`Try to login user '${loginData.userNameOrEmail}'`);
      await this.dialogService.showLoading('Bitte warten...');
      
      // Reset state
      this.saveToken = stayLoggedIn;
      this.setToken(null);      
  
      // Perform login
      const token = await firstValueFrom(this.userRestService.loginUsingPOST(loginData));
            
      // Setup authenticated session
      this.setToken(token);
      this.startTokenRefresher();
      this.islogIn = true;
      
      // Load user data
      await this.dialogService.hideLoading();
      await this.loadAuthenticatedUser(false);
      if (stayLoggedIn) {
        const credentials = {
          username: loginData.userNameOrEmail,
          password: loginData.password,
          rememberMe: stayLoggedIn
        }
        localStorage.setItem('credentials', JSON.stringify(credentials));
      }
      else {
        localStorage.removeItem('credentials');
      }
      
      // await this.router.navigateByUrl('/' + NavUrls.DASHBOARD_MAP);
      return true;
  
    } catch (error) {
      this.log.error('Error on login:', error);
      this.resetTokenAndUser();
      await this.dialogService.hideLoading();
      await this.errHandler.handleHttpError(error);
      
      return false;
    }
  }

  public async tryAutoLogin(): Promise<boolean> {
    const token = await this.storage.get(this.loginTokenName);
    this.log.debug('Try Autologin, saved Token:', token);
    return this.loginWithToken(token);
  }

  public async loginWithToken(token): Promise<boolean> {
    this.log.debug('Try login with token:', token);
    if (token) {
      await this.dialogService.showLoading('Bitte warten...');
      //check if the token is valide!
      // Set the token first even if it can be expired
      //if the token was saved that means the user want to remmeber him
      this.saveToken = true;
      this.setToken(token);
      return new Promise((resolve, reject) => {
        // refresh manually
        this.userRestService.refreshTokenUsingPOST().subscribe(
          async (ret) => {
            this.setToken(ret);
            await this.dialogService.hideLoading();
            this.loadAuthenticatedUser(false)
              .then(() => resolve(true))
              .catch(() => resolve(false));
          },
          async (err) => {
            await this.dialogService.hideLoading();
            this.resetTokenAndUser();
            resolve(false);
          }
        );
      });
    } else {
      return new Promise((resolve, reject) => {
        resolve(false);
      });
    }
  }

  public async logout() {
    this.log.debug('Logout');
    await this.resetTokenAndUser();
   await this.router.navigateByUrl('/' + NavUrls.LOGIN);
  }

  public refreshToken(withuserGrants): Promise<any> {
    return new Promise((resolve, reject) => {
      this.userRestService.refreshTokenUsingPOST().subscribe(
        (ret) => {
          this.setToken(ret);
          if (withuserGrants) {
            this.loadAuthenticatedUser(false)
              .then(() => resolve(true))
              .catch(() => reject(false));
          } else {
            resolve(true);
          }
        },
        (err) => {
          this.logout();
          reject(err);
        }
      );
    });
  }

  public onAuthenticate(): Observable<UserGrantDto> {
    return this.onAuthenticateSubject;
  }
  public getAuthenticatedUser(): UserGrantDto {
    return this.onAuthenticateSubject.getValue();
  }
  public getAuthenticatedUserToken(): ExaTokenDto {
    return this.authenticatedUserToken;
  }
  /**
   * If the user is not authenticated it will redirected to login page
   * if the user has no Grant it will be redirected to Dashboard
   * Dashboard can be access by all Authenticated users
   *
   * @param exaGrant
   * @returns
   */
  checkGrant(exaGrant: ExaGrant) {
    this.log.debug('CheckGrant....', exaGrant);
    const userDto = this.getAuthenticatedUser();
    if (userDto) {
      if (!this.hasGrant(exaGrant)) {
        // this.alertService.presentConfirmAlert("{{'WARNING'|translate}}", "Missing rights for this operation.").then(
        //   buttonClick => {
        //     if(buttonClick == AlertDialogButtonClick.OK){
        console.log(' User fehlen die Rechte dafür.');
        this.router.navigate([NavUrls.DASHBOARD]);
        // this.popoverController.dismiss();
      }
      // });
      // }
    } else {
      this.log.debug('Check.... Not logged in');
      this.alertService.openAlertToast('You are logged out. Please log in.', 2000);
      this.router.navigate([NavUrls.LOGIN]);
    }
  }
  checkGrantOwner(exaGrant: ExaGrant, ownerId: number) {
    this.log.debug('CheckGrantOwner....', exaGrant, ownerId);
    const userGrant = this.getAuthenticatedUser();
    if (userGrant) {
      // const hasGrant = this.hasGrantOwner(exaGrant, ownerId);
      if (!this.hasGrantOwner(exaGrant, ownerId)) {
        this.router.navigate([NavUrls.DASHBOARD]);
      }
    } else {
      this.log.debug('Check.... Not logged in');
      this.router.navigate([NavUrls.LOGIN]);
    }
  }
  hasGrant(exaGrant: ExaGrant): boolean {
    const userGrant = this.getAuthenticatedUser();
    if (userGrant && userGrant.fineGrainedGrants) {
      const grantsToCheck = [];
      grantsToCheck.push(exaGrant);
      if (exaGrant !== ExaGrant.ADMIN) {
        // if we are not checking for ADMIN explicitly -> add it because an ADMIN is allowed to do anything ;)
        grantsToCheck.push(ExaGrant.ADMIN);
      }
      if (exaGrant === ExaGrant.USER) {
        // if we are checking for USER -> also add TECH, TRANSLATION_EDITOR
        //TODO the server musst propagate the TECH and TRANSLATION_EDITOR to USER Grant
        grantsToCheck.push(ExaGrant.TECH);
        grantsToCheck.push(ExaGrant.TRANSLATION_EDITOR);
      }

      // this.log.debug(this.className+ ': user ', userGrant);
      const ret = userGrant.fineGrainedGrants.filter((grant) => grantsToCheck.indexOf(grant) > -1);
      this.log.debug('has grant ', ret);
      return ret.length > 0;
    }
    return false;
  }
  hasGrantOwner(exaGrant: ExaGrant, ownerId: number): boolean {
    if (this.hasGrant(ExaGrant.ADMIN)) {
      // if is admin, everything is allowed ;)
      return true;
    }
    const userGrant = this.getAuthenticatedUser();
    if (userGrant && userGrant.fineGrainedGrants) {
      const grantsToCheck = [];
      if (exaGrant === ExaGrant.ENTERPRISE_DISPONENT) {
        const adminGrantStr = ExaGrant.ENTERPRISE_ADMIN + '-' + ownerId;
        const disponentGrantStr = ExaGrant.ENTERPRISE_DISPONENT + '-' + ownerId;
        grantsToCheck.push(adminGrantStr);
        grantsToCheck.push(disponentGrantStr);
      } else if (exaGrant === ExaGrant.ENTERPRISE_CONSULTANT) {
        const adminGrantStr = ExaGrant.ENTERPRISE_ADMIN + '-' + ownerId;
        const disponentGrantStr = ExaGrant.ENTERPRISE_DISPONENT + '-' + ownerId;
        const consultantGrantStr = ExaGrant.ENTERPRISE_CONSULTANT + '-' + ownerId;
        grantsToCheck.push(adminGrantStr);
        grantsToCheck.push(disponentGrantStr);
        grantsToCheck.push(consultantGrantStr);
      } else if (exaGrant === ExaGrant.ENTERPRISE_DRIVER) {
        const adminGrantStr = ExaGrant.ENTERPRISE_ADMIN + '-' + ownerId;
        const disponentGrantStr = ExaGrant.ENTERPRISE_DISPONENT + '-' + ownerId;
        const consultantGrantStr = ExaGrant.ENTERPRISE_CONSULTANT + '-' + ownerId;
        const driverGrantStr = ExaGrant.ENTERPRISE_DRIVER + '-' + ownerId;
        grantsToCheck.push(adminGrantStr);
        grantsToCheck.push(disponentGrantStr);
        grantsToCheck.push(consultantGrantStr);
        grantsToCheck.push(driverGrantStr);
      } else if (exaGrant === ExaGrant.RETAILER_USER) {
        const adminGrantStr = ExaGrant.RETAILER_ADMIN + '-' + ownerId;
        const userGrantStr = ExaGrant.RETAILER_USER + '-' + ownerId;
        grantsToCheck.push(adminGrantStr);
        grantsToCheck.push(userGrantStr);
      } else {
        // just check for the required grant
        const grantTocheck = exaGrant + '-' + ownerId;
        grantsToCheck.push(grantTocheck);
      }

      // this.log.debug(this.className+ ': user ', userGrant);
      const ret = userGrant.fineGrainedGrants.filter((grant) => grantsToCheck.indexOf(grant) > -1);
      // this.log.debug(this.className+ ': has grant ', ret);
      return ret.length > 0;
    }
    return false;
  }
  private async resetTokenAndUser() {
    this.authUserData = null;
    this.onAuthenticateSubject.next(null);
    this.setToken(null);
    this.selectedEmployer$.next(null);
    await this.storage.set(this.loginTokenName, null);
   await this.storage.set('lastEmployerSelected', null);
   localStorage.removeItem('mapLat');
   localStorage.removeItem('mapLng');
   localStorage.removeItem('mapZoom');
   localStorage.removeItem('bounds');
  localStorage.removeItem('bounds2');
   await this.router.navigateByUrl('/' + NavUrls.LOGIN);
  }
  private async loadAuthenticatedUser(handleRequestError: boolean = true): Promise<void> {
    try {
      await this.dialogService.showLoading('Benutzerdaten werden abgerufen...');
      const userData = await firstValueFrom(this.userRestService.authenticatedUserUsingGET());
      this.authUserData = userData;
      this.onAuthenticateSubject.next(userData);
    } catch (error) {
      this.log.error('Error on loading authenticated user', error);
      if (handleRequestError) {
        await this.errHandler.handleHttpError(error);
      }
      this.onAuthenticateSubject.next(null);
      throw error;
    } finally {
      await this.dialogService.hideLoading(); 
    }
  }

  private startTokenRefresher() {
    if (this.tokenRefresher) {
      clearInterval(this.tokenRefresher);
    }
    this.tokenRefresher = setInterval(() => {
      this.log.debug('Start refresh Token');
      // The user grants my be changed in
      this.refreshToken(true);
    }, environment.refreshTokenTime);
  }
  private setToken(token: ExaTokenDto) {
    // if (this.saveToken) {
      this.storage.set(this.loginTokenName, token);
    // }
    this.authenticatedUserToken = token;
    this.configService.setApiToken(token);
  }
}
